import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Form, Button, Alert,
} from 'react-bootstrap';
import { LoadingAnimation } from '../../../common/components/Utilities';
import TimesheetResource from '../../../common/resource/TimesheetResource';
import UserTimeEntryResource from '../../../common/resource/UserTimeEntryResource';

export const options = {
  mood: {
    ecstatic: {
      icon: '🤩',
      title: 'Ecstatic',
      className: 'text-success',
    },
    happy: {
      icon: '😊',
      title: 'Happy',
      className: 'text-success',
    },
    ok: {
      icon: '😕',
      title: 'OK',
    },
    sad: {
      icon: '😔',
      title: 'Sad',
    },
    tired: {
      icon: '🥱',
      title: 'Tired',
    },
    frustrated: {
      icon: '😤',
      title: 'Frustrated',
      className: 'text-danger',
    },
  },

  engagement: {
    immersed: {
      icon: '🧠',
      title: 'Immersed',
      className: 'text-success',
    },
    productive: {
      icon: '💪',
      title: 'Productive',
      className: 'text-success',
    },
    good: {
      icon: '👍',
      title: 'Good',
      className: 'text-success',
    },
    confused: {
      icon: '🤯',
      title: 'Confused',
    },
    distracted: {
      icon: '🥴',
      title: 'Distracted',
    },
    detached: {
      icon: '😕',
      title: 'Detached',
      className: 'text-danger',
    },
  },

  workload: {
    motivated: {
      icon: '🏃‍♂️',
      title: 'Motivated',
      className: 'text-success',
    },
    content: {
      icon: '😄',
      title: 'Content',
      className: 'text-success',
    },
    bored: {
      icon: '😴',
      title: 'Bored',
    },
    stressed: {
      icon: '😟',
      title: 'Stressed',
      className: 'text-danger',
    },
    overwhelmed: {
      icon: '😣',
      title: 'Overwhelmed',
      className: 'text-danger',
    },
  },
};

function UserCheckout({ user }) {
  const [timeEntryLoading, setTimeEntryLoading] = useState(true);
  const [timesheetLoading, setTimesheetLoading] = useState(true);
  const [savingCheckout, setSavingCheckout] = useState(false);
  const [timesheet, setTimesheet] = useState(null);
  const [message, setMessage] = useState('');
  const [loggedTime, setLoggedTime] = useState(0);
  const [edited, setEdited] = useState(false);
  const [mood, setMood] = useState('');
  const [engagement, setEngagement] = useState('');
  const [workload, setWorkload] = useState('');
  const [checkedOut, setCheckedOut] = useState(false);

  const loadTimesheet = async () => {
    setTimesheetLoading(true);

    const params = {
      user_id: user.id,
      timesheet_date: moment().format('YYYY-MM-DD'),
    };

    try {
      // Search
      const data = await TimesheetResource.index(params);

      if (data.length === 0) {
        // Go create
        const newTimesheet = await TimesheetResource.post(params);
        setTimesheet(newTimesheet);
      } else {
        // Should only be one...
        setTimesheet(data[0]);
      }
    } catch (error) {
      alert(`Something went wrong: ${error}`);
      console.error(error);
    } finally {
      setTimesheetLoading(false);
    }
  };

  const getMessage = (timeEntry) => {
    if (timeEntry.time_type === 'ticket') {
      if (timeEntry.ticket) {
        return `${timeEntry.ticket.jira_key} - ${timeEntry.ticket.title}`;
      }
    }

    if (timeEntry.summary) {
      return timeEntry.summary;
    }

    // Something is wrong here, we shouldn't get this far
    return 'Untitled Time Entry';
  };

  const loadTimeToday = async () => {
    setTimeEntryLoading(true);

    try {
      const filters = {
        start_at_from: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        start_at_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      };
      const data = await UserTimeEntryResource.index(user.id, filters);
      setLoggedTime(data.map((timeEntry) => timeEntry.duration).reduce((a, b) => a + b, 0));
      setMessage(data
        .filter((timeEntry) => timeEntry.summary || timeEntry.ticket)
        .map((timeEntry) => `• ${getMessage(timeEntry)}\n`)
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(''));
    } catch (error) {
      alert(`Something went wrong: ${error}`);
      console.error(error);
    } finally {
      setTimeEntryLoading(false);
    }
  };

  useEffect(() => { loadTimeToday(); loadTimesheet(); }, [user]);

  const doCheckout = async (event) => {
    event.preventDefault();

    setSavingCheckout(true);

    try {
      const params = {
        user_id: user.id,
        check_out: true,
        check_out_summary: message,
        mood,
        engagement,
        workload,
      };

      await TimesheetResource.put(timesheet.id, params);

      setMessage('');
      setCheckedOut(true);
    } catch (error) {
      alert(`Something went wrong: ${error}`);
      console.error(error);
    } finally {
      setSavingCheckout(false);
    }
  };

  return (
    <div>
      <h2>Checkout</h2>
      <LoadingAnimation loading={
          (timesheetLoading || timeEntryLoading || savingCheckout)
      }
      />
      {timesheet && timesheet.check_out_at && (
      <div>
        {'It looks like you\'ve checked out already!'}
      </div>
      )}
      {checkedOut && (
        <div>
          👋 Have a good one!
        </div>
      )}
      {!checkedOut && (timesheet && !timesheet.check_out_at) && (
        <div>
          <div>
            { timesheet && timesheet.check_in_at && (
              <Alert variant="info">
                Recorded check-in at
                {' '}
                {moment(timesheet.check_in_at).format('HH:mm')}
                .
              </Alert>
            )}
            {loggedTime > 0 && (
              <Alert variant="info">
                Time logged today:
                {' '}
                <a href={`/users/${user.id}/timesheets`}>
                  {Math.floor(loggedTime / 60)}
                  {' '}
                  hours
                  {' '}
                  {loggedTime % 60}
                  {' '}
                  minutes.
                </a>
              </Alert>
            )}
          </div>
          <Form onSubmit={doCheckout} className="mb-4">
            <Form.Group>
              <Form.Label>Checkout Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                onChange={(event) => { setMessage(event.target.value); setEdited(true); }}
                value={message}
              />
              {!edited && <Form.Text className="text-muted">Pre-populated with time entries from today</Form.Text>}
              <Form.Text className="text-muted">This will post to your public squad channel(s)</Form.Text>
            </Form.Group>
            <Form.Group>
              <h5>Optionally provide some feedback</h5>

              <Form.Group>
                <Form.Label className="mr-3">How are you feeling in general?</Form.Label>
                {Object.keys(options.mood).map((key) => (
                  <FeedbackOption group="mood" key={key} item={key} onChange={setMood} value={mood} />
                ))}

                <Form.Check
                  inline
                  className="pr-1"
                  type="radio"
                  label="Prefer not to say"
                  value=""
                  onChange={(event) => setMood(event.target.value)}
                  checked={mood === ''}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label className="mr-3">How do you feel about the work?</Form.Label>
                {Object.keys(options.engagement).map((key) => (
                  <FeedbackOption group="engagement" key={key} item={key} onChange={setEngagement} value={engagement} />
                ))}

                <Form.Check
                  inline
                  className="pr-1"
                  type="radio"
                  label="Prefer not to say"
                  value=""
                  onChange={(event) => setEngagement(event.target.value)}
                  checked={engagement === ''}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label className="mr-3">How do you feel about the workload?</Form.Label>
                {Object.keys(options.workload).map((key) => (
                  <FeedbackOption group="workload" key={key} item={key} onChange={setWorkload} value={workload} />
                ))}

                <Form.Check
                  inline
                  className="pr-1"
                  type="radio"
                  label="Prefer not to say"
                  value=""
                  onChange={(event) => setWorkload(event.target.value)}
                  checked={workload === ''}
                />
              </Form.Group>

              <Form.Text className="text-muted">
                {'It\'s useful to get a feeling for how people are finding the work that they\'re doing. This won\'t be shared, or stored against your profile. But it might be used to check trends or stress levels at squad-level during busy periods.'}
              </Form.Text>
            </Form.Group>
            <Button type="submit" disabled={!message || savingCheckout}>Submit</Button>
          </Form>
        </div>

      )}
    </div>
  );
}

function FeedbackOption({
  group,
  item,
  onChange,
  value,
}) {
  return (
    <Form.Check
      inline
      className="pr-1"
      type="radio"
      label={`${options[group][item].icon} ${options[group][item].title}`}
      value={item}
      onChange={(event) => onChange(event.target.value)}
      checked={value === item}
    />
  );
}

export default UserCheckout;
